// Structured block reasons (decision D37 "A block always says what to do"). Every place that blocks a task (or puts it in
// a side state that needs a person) records a stable `code` with typed `params` next to the English `status_reason`
// (kept for logs and "Technical details"). The web builds a translated title, a plain-language explanation and the
// decision buttons from `{code, params}`; the API offers the decisions per code (BLOCK_DECISIONS) with role checks.
import { z } from "zod";

export const BLOCK_REASON_CODES = [
  /** The test/build command could not run (binary missing, sandbox/network unavailable, runner timeout): not the model's fault. */
  "environment",
  /** The premium tier already ran for this task and did not deliver: one automatic premium escalation per task. */
  "quality_after_premium",
  /** The work did not pass tests/review and the agents stopped (no progress, ceiling, escalation not authorized). */
  "quality_stalled",
  /** The council reviewed the block and kept the task blocked. */
  "council_denied",
  /** A hard budget limit (or the reservation it needs) stops new paid work. */
  "budget_limit",
  /** Calls with an unknown cost exist under a hard budget limit (never counted as zero). */
  "unknown_cost",
  /** No model route or connection can serve the task (credential revoked, connection off, inference not allowed). */
  "route_unavailable",
  /** A configuration impediment (for example no critic route for the Gauntlet). */
  "configuration",
  /** No eligible route can hold the mandatory context of the task. */
  "context_capacity",
  /** The provider answered with a different model than the one pinned. */
  "model_divergence",
  /** Hard ceiling of the task run (attempts or model calls). */
  "run_ceiling",
  /** The project cannot run commands yet (no execution opt-in, workspace not ready). */
  "project_not_ready",
  /** The task waits on a dependency that is blocked or failed. */
  "dependency_blocked",
  /** A person must decide (the council escalated an approval request). */
  "approval_needed",
  /** The work conflicts with the base branch. */
  "integration_conflict",
  /** The delivery (pull request, push) failed. */
  "delivery_failed",
  /** A linked fix task carries the work (integration tests or the Maestro rejected the integrated SHA). */
  "fix_task_opened",
  /** The PRD requirement behind the task is ambiguous. */
  "requirement_ambiguous",
  /** A person blocked the task. */
  "manual",
  /** Anything not classified yet (the English reason is still shown under "Technical details"). */
  "unknown",
] as const;
export type BlockReasonCode = (typeof BLOCK_REASON_CODES)[number];

/** Why an environment block happened (classification of the command result before any model escalation). */
export const ENVIRONMENT_FAILURE_KINDS = [
  "command_not_found",
  "not_executable",
  "module_missing",
  "sandbox_unavailable",
  "network_unavailable",
  "runner_timeout",
] as const;
export type EnvironmentFailureKind = (typeof ENVIRONMENT_FAILURE_KINDS)[number];

const id = z.uuid();
const text = (max: number) => z.string().max(max);
const taskRef = z.object({ id: id.optional(), number: z.number().int().positive() });

const environmentParams = z.object({
  kind: z.enum(ENVIRONMENT_FAILURE_KINDS),
  command: z.enum(["prepare", "build", "test"]).default("test"),
  /** Where the command ran: the task's gate, or the integration gate on the merged result. */
  stage: z.enum(["gate", "integration"]).default("gate"),
  exit_code: z.number().int().nullable().default(null),
  binary: text(100).nullable().default(null),
  evidence_id: id.nullable().default(null),
  /** Last lines of the command output (bounded; never secrets: the sandbox has none). */
  output_tail: text(800).nullable().default(null),
});

const qualityParams = z.object({
  /** Escalation reason / stop cause of the engine (e.g. quality_gate_failed, no_progress). */
  cause: text(200).nullable().default(null),
  attempts: z.number().int().nonnegative().nullable().default(null),
});

const councilParams = z.object({
  verdict: text(40).nullable().default(null),
  reasons: z.array(text(300)).max(5).default([]),
  /** Code of the block the council reviewed. */
  previous_code: z.enum(BLOCK_REASON_CODES).nullable().default(null),
});

const budgetParams = z.object({
  scope: text(40).nullable().default(null),
  limit_cents: z.number().int().nullable().default(null),
  committed_cents: z.number().int().nullable().default(null),
  unknown_calls: z.number().int().nullable().default(null),
});

const ceilingParams = z.object({
  limit_kind: z.enum(["attempts", "model_calls", "other"]).default("other"),
  used: z.number().int().nullable().default(null),
  limit: z.number().int().nullable().default(null),
});

const detailParams = z.object({ detail: text(300).nullable().default(null) });

export const blockReasonSchema = z.discriminatedUnion("code", [
  z.object({ code: z.literal("environment"), params: environmentParams }),
  z.object({ code: z.literal("quality_after_premium"), params: qualityParams }),
  z.object({ code: z.literal("quality_stalled"), params: qualityParams }),
  z.object({ code: z.literal("council_denied"), params: councilParams }),
  z.object({ code: z.literal("budget_limit"), params: budgetParams }),
  z.object({ code: z.literal("unknown_cost"), params: budgetParams }),
  z.object({ code: z.literal("route_unavailable"), params: detailParams }),
  z.object({ code: z.literal("configuration"), params: detailParams }),
  z.object({ code: z.literal("context_capacity"), params: detailParams }),
  z.object({ code: z.literal("model_divergence"), params: detailParams }),
  z.object({ code: z.literal("run_ceiling"), params: ceilingParams }),
  z.object({ code: z.literal("project_not_ready"), params: detailParams }),
  z.object({ code: z.literal("dependency_blocked"), params: z.object({ blocking: z.array(taskRef).max(20).default([]) }) }),
  z.object({ code: z.literal("approval_needed"), params: z.object({ approval_code: text(40).nullable().default(null) }) }),
  z.object({ code: z.literal("integration_conflict"), params: z.object({ files: z.array(text(300)).max(20).default([]), base_branch: text(200).nullable().default(null) }) }),
  z.object({ code: z.literal("delivery_failed"), params: detailParams }),
  z.object({ code: z.literal("fix_task_opened"), params: z.object({ fix_task_number: z.number().int().positive().nullable().default(null) }) }),
  z.object({ code: z.literal("requirement_ambiguous"), params: detailParams }),
  z.object({ code: z.literal("manual"), params: detailParams }),
  z.object({ code: z.literal("unknown"), params: detailParams }),
]);
export type BlockReason = z.infer<typeof blockReasonSchema>;
export type BlockReasonOf<C extends BlockReasonCode> = Extract<BlockReason, { code: C }>;

/**
 * Normalizes a stored/derived `{code, params}` (defaults filled, unknown fields dropped); an invalid value becomes
 * `unknown` so a bad row never breaks a read.
 */
export function normalizeBlockReason(value: { code: string; params?: unknown } | null | undefined): BlockReason | null {
  if (!value) return null;
  const parsed = blockReasonSchema.safeParse({ code: value.code, params: value.params ?? {} });
  return parsed.success ? parsed.data : { code: "unknown", params: { detail: null } };
}

// ---------- Decisions offered per code ----------

/**
 * Decisions of a block. `server` decisions are one POST /v1/tasks/{id}/block-decisions with role checks and an audit
 * event; `client` decisions only navigate (settings, the blocking task) or open the assistant with the task context.
 */
export const BLOCK_DECISIONS = [
  // server
  "retry_now",
  "accept_without_tests",
  "retry_same_model",
  "retry_other_model",
  "cancel_task",
  "override_retry",
  "release_dependents",
  "approve_cost",
  "reconcile_cost",
  // client
  "edit_test_command",
  "ask_assistant",
  "edit_task",
  "split_task",
  "open_blocking_task",
  "raise_limit",
  "open_settings",
] as const;
export type BlockDecision = (typeof BLOCK_DECISIONS)[number];

export const SERVER_BLOCK_DECISIONS = [
  "retry_now",
  "accept_without_tests",
  "retry_same_model",
  "retry_other_model",
  "cancel_task",
  "override_retry",
  "release_dependents",
  "approve_cost",
  "reconcile_cost",
] as const satisfies readonly BlockDecision[];
export type ServerBlockDecision = (typeof SERVER_BLOCK_DECISIONS)[number];

export const blockDecisionOptionSchema = z.object({
  decision: z.enum(BLOCK_DECISIONS),
  kind: z.enum(["server", "client"]),
  /** Minimum project role (server decisions; client decisions name the role of what they open). */
  min_role: z.enum(["observer", "reviewer", "operator", "maintainer", "admin"]),
  /** The caller holds the role (the web shows the button disabled with the reason otherwise). */
  allowed: z.boolean(),
  /** Destructive or sensitive: the web asks for a confirmation first. */
  confirm: z.boolean(),
  /** Primary decision of the code (highlighted). */
  primary: z.boolean(),
});
export type BlockDecisionOption = z.infer<typeof blockDecisionOptionSchema>;

export const blockDecisionRequestSchema = z.object({
  decision: z.enum(SERVER_BLOCK_DECISIONS),
  expected_version: z.number().int().nonnegative(),
  /** Required for the confirmed decisions (the web sends it after the dialog). */
  confirmed: z.boolean().default(false),
  /** retry_other_model: a verified model target. */
  model_target_id: id.optional(),
  /** approve_cost: the cost ceiling approved for the next attempt (cents). */
  max_cost_cents: z.number().int().positive().max(1_000_000).optional(),
  /**
   * reconcile_cost (D41-A3): the actual charge of each paid call whose cost is unknown, as read by an administrator on
   * the provider's records (0 when the provider did not charge). Never inferred; the note names the evidence checked.
   */
  reconciled_calls: z
    .array(z.object({ model_call_id: id, cost_cents: z.number().int().nonnegative().max(1_000_000) }))
    .min(1)
    .max(50)
    .optional(),
  note: z.string().max(500).optional(),
});
export type BlockDecisionRequest = z.infer<typeof blockDecisionRequestSchema>;

const taskBrief = z.object({ id, number: z.number().int(), title: z.string(), status: z.string() });

/** GET /v1/tasks/{id}/block: everything the blocked card, the modal and the banner need. */
export const taskBlockViewSchema = z.object({
  task_id: id,
  task_number: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  block_reason: blockReasonSchema.nullable(),
  /** The English reason (logs, "Technical details"). */
  raw_reason: z.string().nullable(),
  /** Open tasks that wait for this one, directly or through a chain. */
  dependents: z.array(taskBrief),
  /** Unfinished dependencies of this task that are blocked or failed. */
  blocking: z.array(taskBrief),
  evidence: z
    .object({ id, summary: z.string(), output_tail: z.string().nullable(), created_at: z.iso.datetime() })
    .nullable(),
  decisions: z.array(blockDecisionOptionSchema),
  /** Verified model targets for "Retry with another model" (empty when the decision is not offered). */
  model_targets: z.array(z.object({ id, label: z.string(), tier: z.string().nullable() })),
  /** Paid calls of the project whose cost is unknown (what "Reconcile cost" settles; empty otherwise). */
  unknown_cost_calls: z
    .array(
      z.object({
        id,
        model: z.string().nullable(),
        provider: z.string().nullable(),
        state: z.string(),
        error_class: z.string().nullable(),
        created_at: z.iso.datetime(),
      }),
    )
    .default([]),
  current_model_target_id: id.nullable().optional(),
  /** Operational record of the active block: who acts next, what, and what resumes the task. */
  action: z
    .object({
      owner_kind: z.enum(["system", "agent", "human", "integration"]),
      next_action: z.string(),
      resume_condition: z.string(),
      attempts: z.number().int().nullable(),
      created_at: z.iso.datetime(),
    })
    .nullable()
    .optional(),
});
export type TaskBlockView = z.infer<typeof taskBlockViewSchema>;

export const blockDecisionResultSchema = z.object({
  decision: z.enum(SERVER_BLOCK_DECISIONS),
  task_id: id,
  status: z.string(),
  version: z.number().int(),
  /** A task run was started (or was already running) after the decision. */
  run_started: z.boolean(),
  /** Dependents released by release_dependents. */
  released: z.array(z.number().int()).default([]),
  message: z.string().nullable(),
});
export type BlockDecisionResult = z.infer<typeof blockDecisionResultSchema>;
